import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2, Save, ChevronDown, ChevronUp, Mail, Clock, Eye, Edit3, Copy, Loader2, ArrowLeft, Variable, RefreshCw, GitCompare, Check, AlertTriangle, Undo2 } from 'lucide-react';
import { fetchWithAuth } from '../pages/IntelligenceLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VARIABLES = [
  { key: '{{first_name}}', label: 'First Name' },
  { key: '{{last_name}}', label: 'Last Name' },
  { key: '{{company_name}}', label: 'Company' },
  { key: '{{title}}', label: 'Title' },
  { key: '{{score}}', label: 'Score' },
  { key: '{{sender_name}}', label: 'Sender Name' },
];

const DELAY_UNITS = ['days', 'hours', 'minutes'];
const TONES = ['casual', 'direct', 'professional'];

/* ── Variable Inserter ── */
const VariableInserter = ({ onInsert }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button type="button" onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-[#0A1F44] bg-gray-100 hover:bg-gray-200 rounded transition-colors"
        data-testid="var-inserter-btn">
        <Variable className="h-3 w-3" /> Insert Variable
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[160px]" data-testid="var-dropdown">
          {VARIABLES.map(v => (
            <button key={v.key} type="button"
              onClick={() => { onInsert(v.key); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between">
              <span className="text-gray-700">{v.label}</span>
              <code className="text-[10px] text-gray-400 ml-2">{v.key}</code>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Overwrite Warning Modal ── */
const OverwriteWarning = ({ onPreserve, onReplace, onAlternate, onCancel }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" data-testid="overwrite-warning">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-500" />
        <h3 className="text-sm font-bold text-[#0A1F44]">This step has manual edits</h3>
      </div>
      <p className="text-xs text-gray-600">You've manually edited this step. How would you like to proceed?</p>
      <div className="space-y-2">
        <button onClick={onPreserve}
          className="w-full px-4 py-2.5 text-left text-xs border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          data-testid="overwrite-preserve">
          <span className="font-semibold text-[#0A1F44]">Regenerate using my edits as guidance</span>
          <p className="text-gray-500 mt-0.5">AI will preserve your structure and CTA style</p>
        </button>
        <button onClick={onReplace}
          className="w-full px-4 py-2.5 text-left text-xs border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          data-testid="overwrite-replace">
          <span className="font-semibold text-[#0A1F44]">Regenerate from original prompt</span>
          <p className="text-gray-500 mt-0.5">Discard edits and create a fresh AI draft</p>
        </button>
        <button onClick={onAlternate}
          className="w-full px-4 py-2.5 text-left text-xs border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          data-testid="overwrite-alternate">
          <span className="font-semibold text-[#0A1F44]">Create alternate version</span>
          <p className="text-gray-500 mt-0.5">Keep current version, generate a new one alongside it</p>
        </button>
      </div>
      <button onClick={onCancel} className="w-full px-4 py-2 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
    </div>
  </div>
);

/* ── Version Compare ── */
const VersionCompare = ({ versions, activeIndex, onSetActive, onClose }) => {
  const [compareA, setCompareA] = useState(0);
  const [compareB, setCompareB] = useState(Math.min(1, versions.length - 1));
  const vA = versions[compareA] || {};
  const vB = versions[compareB] || {};

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" data-testid="version-compare">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
          <h3 className="text-sm font-bold text-[#0A1F44]">Compare Versions</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-4">
            {[{ idx: compareA, set: setCompareA, v: vA }, { idx: compareB, set: setCompareB, v: vB }].map(({ idx, set, v }, col) => (
              <div key={col} className="space-y-3">
                <div className="flex items-center gap-2">
                  <select value={idx} onChange={e => set(Number(e.target.value))}
                    className="text-xs border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none">
                    {versions.map((ver, i) => (
                      <option key={i} value={i}>{ver.label || `v${i + 1}`} {i === activeIndex ? '(Active)' : ''}</option>
                    ))}
                  </select>
                  {idx === activeIndex && (
                    <span className="px-1.5 py-0.5 bg-green-50 text-green-700 text-[9px] font-bold rounded">ACTIVE</span>
                  )}
                  {idx !== activeIndex && (
                    <button onClick={() => onSetActive(idx)}
                      className="px-2 py-0.5 bg-[#D4AF37] text-[#0A1F44] text-[9px] font-bold rounded hover:bg-[#c5a030]"
                      data-testid={`set-active-v${idx}`}>
                      Set Active
                    </button>
                  )}
                </div>
                <div className="text-[10px] text-gray-400">
                  {v.type === 'ai_original' && 'AI Original Draft'}
                  {v.type === 'user_edited' && 'Your Manual Edits'}
                  {v.type === 'regenerated' && `Regenerated (${v.mode || 'from_edited'})`}
                  {v.type === 'alternate' && 'Alternate Version'}
                  {v.tone && ` | Tone: ${v.tone}`}
                </div>
                <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <p className="text-[10px] text-gray-400 mb-1">Subject</p>
                  <p className="text-sm font-medium text-[#0A1F44]">{v.subject || '(empty)'}</p>
                </div>
                <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <p className="text-[10px] text-gray-400 mb-1">Body</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{v.body || '(empty)'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Step Editor with Version Management ── */
const StepEditor = ({ step, index, total, onChange, onRemove, onMoveUp, onMoveDown, companyName, onRegenerate }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showOverwriteWarning, setShowOverwriteWarning] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [toneForRegen, setToneForRegen] = useState('professional');

  // Version management
  const versions = step.versions || [];
  const activeIdx = step.active_version_index ?? 0;
  const activeVersion = versions[activeIdx] || { subject: '', body: '' };

  // Current editing state uses active version
  const currentSubject = activeVersion.subject || '';
  const currentBody = activeVersion.body || '';

  // Track if user has made edits vs the original
  const originalVersion = versions.find(v => v.type === 'ai_original');
  const hasUserEdits = step._user_dirty || (originalVersion &&
    (currentSubject !== originalVersion.subject || currentBody !== originalVersion.body));

  const updateActiveVersion = (field, value) => {
    const newVersions = [...versions];
    if (!newVersions[activeIdx]) return;

    // Mark as user-edited if changing from AI original
    const oldType = newVersions[activeIdx].type;
    newVersions[activeIdx] = {
      ...newVersions[activeIdx],
      [field]: value,
    };
    if (oldType === 'ai_original' || oldType === 'regenerated') {
      newVersions[activeIdx].type = 'user_edited';
      newVersions[activeIdx].edited_at = new Date().toISOString();
    }

    // Also update the legacy variants array for backwards compat
    const variants = (step.variants || [{ subject: '', body: '' }]).map((v, i) =>
      i === 0 ? { ...v, [field]: value } : v
    );

    onChange({ ...step, versions: newVersions, variants, _user_dirty: true });
  };

  const handleRegenClick = () => {
    if (hasUserEdits) {
      setShowOverwriteWarning(true);
    } else {
      // No edits, regenerate freely
      doRegenerate('from_original');
    }
  };

  const doRegenerate = async (mode) => {
    setShowOverwriteWarning(false);
    setRegenerating(true);

    try {
      const resp = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/ai/regenerate-step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: companyName || 'Target Company',
          step_index: index,
          current_subject: currentSubject,
          current_body: currentBody,
          original_subject: originalVersion?.subject || '',
          original_body: originalVersion?.body || '',
          mode,
          tone: toneForRegen,
        }),
      });

      if (resp.error) {
        toast.error(resp.detail || 'Regeneration failed');
        return;
      }

      const newVersion = {
        type: mode === 'alternate' ? 'alternate' : 'regenerated',
        subject: resp.subject,
        body: resp.body,
        mode,
        tone: toneForRegen,
        generated_at: resp.generated_at || new Date().toISOString(),
        label: mode === 'alternate'
          ? `Alt v${versions.length + 1}`
          : `Regen (${mode === 'from_edited' ? 'from edits' : 'fresh'})`,
      };

      const newVersions = [...versions, newVersion];
      const newActiveIdx = mode === 'alternate' ? activeIdx : newVersions.length - 1;

      // Update variants for backwards compat
      const targetVersion = newVersions[newActiveIdx];
      const variants = [{ subject: targetVersion.subject, body: targetVersion.body }];

      onChange({
        ...step,
        versions: newVersions,
        active_version_index: newActiveIdx,
        variants,
        _user_dirty: false,
      });

      toast.success(mode === 'alternate' ? 'Alternate version created' : 'Step regenerated');
    } catch (err) {
      toast.error('Regeneration failed');
    } finally {
      setRegenerating(false);
    }
  };

  const setActiveVersion = (idx) => {
    const v = versions[idx];
    if (!v) return;
    const variants = [{ subject: v.subject, body: v.body }];
    onChange({
      ...step,
      active_version_index: idx,
      variants,
      _user_dirty: false,
    });
  };

  const resetToOriginal = () => {
    const origIdx = versions.findIndex(v => v.type === 'ai_original');
    if (origIdx >= 0) {
      setActiveVersion(origIdx);
      toast.success('Reset to original AI draft');
    }
  };

  const insertVariable = (field, variable) => {
    const elId = `step-${index}-${field}`;
    const el = document.getElementById(elId);
    if (el) {
      const start = el.selectionStart || el.value.length;
      const newVal = el.value.slice(0, start) + variable + el.value.slice(start);
      updateActiveVersion(field, newVal);
      setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = start + variable.length; }, 50);
    } else {
      updateActiveVersion(field, currentBody + variable);
    }
  };

  return (
    <>
      <div className="border border-gray-200 rounded-lg bg-white" data-testid={`step-editor-${index}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 rounded-t-lg cursor-pointer"
          onClick={() => setCollapsed(!collapsed)}>
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 bg-[#0A1F44] rounded-full flex items-center justify-center">
              <span className="text-[10px] font-bold text-[#D4AF37]">{index + 1}</span>
            </div>
            <span className="text-xs font-semibold text-[#0A1F44]">
              Step {index + 1}: {currentSubject || 'Untitled Email'}
            </span>
            <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[9px] font-medium rounded">
              {step.delay} {step.delay_unit} delay
            </span>
            {hasUserEdits && (
              <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[9px] font-medium rounded">Edited</span>
            )}
            {versions.length > 1 && (
              <span className="px-1.5 py-0.5 bg-purple-50 text-purple-600 text-[9px] font-medium rounded">
                {versions.length} versions
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {index > 0 && (
              <button type="button" onClick={e => { e.stopPropagation(); onMoveUp(); }}
                className="p-1 hover:bg-gray-200 rounded" title="Move up">
                <ChevronUp className="h-3 w-3 text-gray-400" />
              </button>
            )}
            {index < total - 1 && (
              <button type="button" onClick={e => { e.stopPropagation(); onMoveDown(); }}
                className="p-1 hover:bg-gray-200 rounded" title="Move down">
                <ChevronDown className="h-3 w-3 text-gray-400" />
              </button>
            )}
            <button type="button" onClick={e => { e.stopPropagation(); onRemove(); }}
              className="p-1 hover:bg-red-50 rounded" title="Remove step"
              data-testid={`remove-step-${index}`}>
              <Trash2 className="h-3 w-3 text-red-400" />
            </button>
            {collapsed ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronUp className="h-4 w-4 text-gray-400" />}
          </div>
        </div>

        {!collapsed && (
          <div className="p-4 space-y-3">
            {/* Delay */}
            <div className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-xs text-gray-600">Wait</span>
              <input type="number" min={0} value={step.delay}
                onChange={e => onChange({ ...step, delay: parseInt(e.target.value) || 0 })}
                className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none"
                data-testid={`step-${index}-delay`} />
              <select value={step.delay_unit}
                onChange={e => onChange({ ...step, delay_unit: e.target.value })}
                className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none"
                data-testid={`step-${index}-delay-unit`}>
                {DELAY_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>

            {/* Subject */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">Subject Line</label>
                <VariableInserter onInsert={v => insertVariable('subject', v)} />
              </div>
              <input id={`step-${index}-subject`} type="text" value={currentSubject}
                onChange={e => updateActiveVersion('subject', e.target.value)}
                placeholder="Enter subject line..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none"
                data-testid={`step-${index}-subject`} />
            </div>

            {/* Body */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">Email Body</label>
                <VariableInserter onInsert={v => insertVariable('body', v)} />
              </div>
              <textarea id={`step-${index}-body`} value={currentBody}
                onChange={e => updateActiveVersion('body', e.target.value)}
                placeholder="Write your email body..."
                rows={6}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none resize-y font-mono"
                data-testid={`step-${index}-body`} />
            </div>

            {/* Step-level controls */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <div className="flex items-center gap-2">
                {/* Regenerate */}
                <div className="flex items-center gap-1">
                  <select value={toneForRegen} onChange={e => setToneForRegen(e.target.value)}
                    className="px-2 py-1 text-[10px] border border-gray-300 rounded focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none"
                    data-testid={`step-${index}-tone`}>
                    {TONES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                  <button type="button" onClick={handleRegenClick} disabled={regenerating}
                    className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium bg-[#0A1F44] text-white rounded hover:bg-[#0d2a5c] disabled:opacity-50 transition-colors"
                    data-testid={`step-${index}-regenerate`}>
                    {regenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    Regenerate
                  </button>
                </div>

                {/* Reset to original */}
                {originalVersion && hasUserEdits && (
                  <button type="button" onClick={resetToOriginal}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                    data-testid={`step-${index}-reset`}>
                    <Undo2 className="h-3 w-3" /> Reset to Original
                  </button>
                )}

                {/* Compare versions */}
                {versions.length > 1 && (
                  <button type="button" onClick={() => setShowCompare(true)}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-purple-600 border border-purple-200 rounded hover:bg-purple-50 transition-colors"
                    data-testid={`step-${index}-compare`}>
                    <GitCompare className="h-3 w-3" /> Compare ({versions.length})
                  </button>
                )}
              </div>

              {/* Active version indicator */}
              {versions.length > 1 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-400">Active:</span>
                  <select value={activeIdx} onChange={e => setActiveVersion(Number(e.target.value))}
                    className="px-2 py-0.5 text-[10px] border border-gray-300 rounded focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none"
                    data-testid={`step-${index}-active-version`}>
                    {versions.map((v, i) => (
                      <option key={i} value={i}>{v.label || `v${i + 1} (${v.type})`}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showOverwriteWarning && (
        <OverwriteWarning
          onPreserve={() => doRegenerate('from_edited')}
          onReplace={() => doRegenerate('from_original')}
          onAlternate={() => doRegenerate('alternate')}
          onCancel={() => setShowOverwriteWarning(false)}
        />
      )}
      {showCompare && (
        <VersionCompare
          versions={versions}
          activeIndex={activeIdx}
          onSetActive={(idx) => { setActiveVersion(idx); setShowCompare(false); }}
          onClose={() => setShowCompare(false)}
        />
      )}
    </>
  );
};

/* ── Email Preview ── */
const EmailPreview = ({ step, index }) => {
  const versions = step.versions || [];
  const activeIdx = step.active_version_index ?? 0;
  const v = versions[activeIdx] || step.variants?.[0] || {};

  const preview = (text) => (text || '').replace(/\{\{(\w+)\}\}/g, (_, k) => {
    const map = { first_name: 'John', last_name: 'Smith', company_name: 'Acme Corp', title: 'VP of HR', score: '15', sender_name: 'Your Name' };
    return `<span class="px-0.5 bg-[#D4AF37]/20 text-[#0A1F44] font-medium rounded">${map[k] || k}</span>`;
  });

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid={`preview-step-${index}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="h-5 w-5 bg-[#0A1F44] rounded-full flex items-center justify-center">
          <span className="text-[9px] font-bold text-[#D4AF37]">{index + 1}</span>
        </div>
        <span className="text-[10px] text-gray-400">Step {index + 1} ({step.delay} {step.delay_unit} delay)</span>
        {v.type && <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[9px] rounded">{v.type}</span>}
      </div>
      <div className="mb-2">
        <span className="text-[10px] text-gray-400">Subject: </span>
        <span className="text-sm font-medium text-[#0A1F44]" dangerouslySetInnerHTML={{ __html: preview(v.subject) }} />
      </div>
      <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700 whitespace-pre-wrap leading-relaxed"
        dangerouslySetInnerHTML={{ __html: preview(v.body) }} />
    </div>
  );
};

/* ── Main Template Editor ── */
export const TemplateEditor = ({ templateId, onClose, onSaved, companyName }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(!!templateId);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('edit');
  const [isBuiltin, setIsBuiltin] = useState(false);

  // Load existing template and initialize version tracking
  useEffect(() => {
    if (!templateId) {
      setSteps([{
        type: 'email', delay: 0, delay_unit: 'days',
        variants: [{ subject: '', body: '' }],
        versions: [{ type: 'user_edited', subject: '', body: '', label: 'v1 (draft)' }],
        active_version_index: 0,
      }]);
      return;
    }
    fetchWithAuth(`${API_URL}/api/intelligence/acquisition/sequence-templates/${templateId}`)
      .then(data => {
        if (data.error) { toast.error('Template not found'); onClose(); return; }
        setName(data.name || '');
        setDescription(data.description || '');
        setIsBuiltin(!!data.is_builtin);
        const seq = data.sequences?.[0];
        if (seq?.steps) {
          // Initialize version tracking for each step
          const enrichedSteps = seq.steps.map((s, i) => {
            const variant = s.variants?.[0] || {};
            const existingVersions = s.versions || [];
            if (existingVersions.length === 0) {
              // First load — create the initial version entry
              return {
                ...s,
                versions: [{
                  type: data.ai_generated ? 'ai_original' : 'user_edited',
                  subject: variant.subject || '',
                  body: variant.body || '',
                  label: data.ai_generated ? 'v1 (AI original)' : 'v1 (original)',
                  created_at: data.created_at || new Date().toISOString(),
                  tone: 'professional',
                }],
                active_version_index: 0,
              };
            }
            return s;
          });
          setSteps(enrichedSteps);
        }
      })
      .catch(() => toast.error('Failed to load template'))
      .finally(() => setLoading(false));
  }, [templateId, onClose]);

  const addStep = () => {
    setSteps(prev => [...prev, {
      type: 'email', delay: 2, delay_unit: 'days',
      variants: [{ subject: '', body: '' }],
      versions: [{ type: 'user_edited', subject: '', body: '', label: `v1 (draft)` }],
      active_version_index: 0,
    }]);
  };

  const updateStep = (index, updated) => {
    setSteps(prev => prev.map((s, i) => i === index ? updated : s));
  };

  const removeStep = (index) => {
    if (steps.length <= 1) { toast.error('Need at least one step'); return; }
    setSteps(prev => prev.filter((_, i) => i !== index));
  };

  const moveStep = (from, to) => {
    setSteps(prev => {
      const arr = [...prev];
      const [item] = arr.splice(from, 1);
      arr.splice(to, 0, item);
      return arr;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Template name is required'); return; }
    if (steps.length === 0) { toast.error('Add at least one step'); return; }
    for (let i = 0; i < steps.length; i++) {
      const versions = steps[i].versions || [];
      const active = versions[steps[i].active_version_index ?? 0];
      if (!active?.subject?.trim() || !active?.body?.trim()) {
        toast.error(`Step ${i + 1} needs a subject and body`);
        return;
      }
    }
    setSaving(true);

    // Build sequences payload with version data included
    const cleanSteps = steps.map(s => {
      const versions = s.versions || [];
      const activeIdx = s.active_version_index ?? 0;
      const active = versions[activeIdx] || {};
      return {
        type: s.type || 'email',
        delay: s.delay,
        delay_unit: s.delay_unit,
        variants: [{ subject: active.subject, body: active.body }],
        versions: versions.map(v => ({
          type: v.type,
          subject: v.subject,
          body: v.body,
          label: v.label,
          tone: v.tone,
          mode: v.mode,
          created_at: v.created_at,
          edited_at: v.edited_at,
          generated_at: v.generated_at,
        })),
        active_version_index: activeIdx,
      };
    });

    const payload = {
      name: name.trim(),
      description: description.trim(),
      sequences: [{ steps: cleanSteps }],
    };
    try {
      let data;
      if (templateId) {
        data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/sequence-templates/${templateId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/sequence-templates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      if (data.success) {
        toast.success(isBuiltin ? 'Saved as new custom template' : (templateId ? 'Template updated' : 'Template created'));
        onSaved();
        onClose();
      } else {
        toast.error(data.detail || 'Failed to save');
      }
    } catch { toast.error('Failed to save template'); }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" /></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="template-editor-modal">
      <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-xl">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="editor-close-btn">
              <ArrowLeft className="h-4 w-4 text-gray-400" />
            </button>
            <div>
              <h2 className="text-base font-bold text-[#0A1F44]">
                {templateId ? (isBuiltin ? 'Customize Template' : 'Edit Template') : 'Create Template'}
              </h2>
              {isBuiltin && (
                <p className="text-[10px] text-amber-600 font-medium">Editing a built-in template will save as a new custom copy</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-gray-100 rounded-lg p-0.5" data-testid="editor-mode-toggle">
              <button onClick={() => setMode('edit')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${mode === 'edit' ? 'bg-white text-[#0A1F44] shadow-sm' : 'text-gray-500'}`}
                data-testid="mode-edit">
                <Edit3 className="h-3 w-3 inline mr-1" />Edit
              </button>
              <button onClick={() => setMode('preview')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${mode === 'preview' ? 'bg-white text-[#0A1F44] shadow-sm' : 'text-gray-500'}`}
                data-testid="mode-preview">
                <Eye className="h-3 w-3 inline mr-1" />Preview
              </button>
            </div>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 bg-[#D4AF37] text-[#0A1F44] text-xs font-bold rounded-lg hover:bg-[#c5a030] disabled:opacity-50 flex items-center gap-1"
              data-testid="save-template-btn">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Save Template
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {mode === 'edit' ? (
            <>
              {/* Name & Description */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1 block">Template Name</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)}
                    placeholder="e.g., Enterprise Client Outreach"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none"
                    data-testid="template-name-input" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1 block">Description</label>
                  <input type="text" value={description} onChange={e => setDescription(e.target.value)}
                    placeholder="Brief description of this sequence..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none"
                    data-testid="template-desc-input" />
                </div>
              </div>

              {/* Steps */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-[#0A1F44]">Email Steps ({steps.length})</h3>
                  <button type="button" onClick={addStep}
                    className="flex items-center gap-1 px-3 py-1.5 bg-[#0A1F44] text-white text-[10px] font-medium rounded-lg hover:bg-[#0d2a5c]"
                    data-testid="add-step-btn">
                    <Plus className="h-3 w-3" /> Add Step
                  </button>
                </div>
                {steps.map((step, i) => (
                  <StepEditor
                    key={i}
                    step={step}
                    index={i}
                    total={steps.length}
                    onChange={updated => updateStep(i, updated)}
                    onRemove={() => removeStep(i)}
                    onMoveUp={() => moveStep(i, i - 1)}
                    onMoveDown={() => moveStep(i, i + 1)}
                    companyName={companyName}
                  />
                ))}
              </div>

              {/* Variable Reference */}
              <div className="p-3 bg-white border border-gray-200 rounded-lg">
                <p className="text-[10px] text-gray-500 font-medium mb-1.5">AVAILABLE VARIABLES</p>
                <div className="flex flex-wrap gap-1.5">
                  {VARIABLES.map(v => (
                    <code key={v.key} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-[10px] rounded">
                      {v.key} — {v.label}
                    </code>
                  ))}
                </div>
              </div>
            </>
          ) : (
            /* Preview Mode */
            <div className="space-y-4">
              <div className="p-3 bg-white border border-gray-200 rounded-lg">
                <h3 className="text-sm font-bold text-[#0A1F44]">{name || 'Untitled Template'}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{description || 'No description'}</p>
                <div className="flex gap-3 mt-2">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">{steps.length} steps</span>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">
                    {steps.reduce((sum, s) => sum + (s.delay_unit === 'days' ? s.delay : 0), 0)} days total
                  </span>
                </div>
              </div>
              {steps.map((step, i) => (
                <EmailPreview key={i} step={step} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;
